<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

const props = defineProps({
  featureIds: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['update:added'])

const store = useStore()
const member = computed(() => store.getters['user/getMember'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])

const isOpen = ref(false)
const staticLayers = ref([])
const selectedLayerId = ref(null)
const isLoading = ref(false)
const isAddingFeatures = ref(false)

const open = () => {
  isOpen.value = true
  fetchStaticLayers()
}

const close = () => {
  isOpen.value = false
  selectedLayerId.value = null
}

const fetchStaticLayers = async () => {
  isLoading.value = true
  try {
    const url = `${HOST}/org/${selectedOrg.value.organization_id}/layers/all/static`

    const response = await API({
      method: 'GET',
      url: url,
    })

    staticLayers.value = response.data || []
  } catch (error) {
    // Error handled silently
    toast({
      title: 'Error fetching static layers',
      description: error.response?.data?.detail || 'Could not load available static layers'
    })
  }
  isLoading.value = false
}

const addToStaticLayer = async () => {
  if (!selectedLayerId.value || !props.featureIds.length) return

  const selectedLayer = staticLayers.value.find(layer => layer.code === selectedLayerId.value)
  if (!selectedLayer) return

  isAddingFeatures.value = true
  try {
    const response = await API({
      method: 'PATCH',
      url: `${HOST}/org/${selectedOrg.value.organization_id}/layers/${selectedLayerId.value}/static/add-features`,
      data: {
        feature_ids: props.featureIds
      }
    })

    toast({
      title: 'Features added successfully',
      description: `Added ${props.featureIds.length} feature(s) to "${selectedLayer.name}"`
    })

    emit('update:added', {
      layerCode: selectedLayerId.value,
      layerName: selectedLayer.name,
      addedFeatures: props.featureIds
    })

    close()
  } catch (error) {
    console.error('Error adding features to static layer:', error)
    toast({
      title: 'Error adding features',
      description: error.response?.data?.detail || 'Could not add features to static layer'
    })
  }
  isAddingFeatures.value = false
}

defineExpose({
  open
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogContent class="max-w-[450px]">
      <DialogHeader>
        <DialogTitle>Save to Folder</DialogTitle>
      </DialogHeader>

      <div class="space-y-4">
        <div v-if="isLoading" class="flex justify-center py-8">
          <Spinner class="w-8 h-8" />
        </div>

        <div v-else-if="staticLayers.length === 0" class="text-center py-8 text-gray-500">
          <p>No static layers found.</p>
          <p class="text-sm">Create a static layer first to add features to it.</p>
        </div>

        <div v-else class="space-y-3">
          <RadioGroup v-model="selectedLayerId" class="space-y-2">
            <div
              v-for="layer in staticLayers"
              :key="layer.code"
              class="flex items-center space-x-2 rounded-lg hover:bg-gray-50"
            >
              <RadioGroupItem :value="layer.code" :id="layer.code" />
              <Label :for="layer.code" class="flex-1 cursor-pointer">
                <div class="font-medium">{{ layer.name }}</div>
              </Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      <DialogFooter>
        <Button
          @click="addToStaticLayer"
          :disabled="!selectedLayerId || isAddingFeatures"
        >
          <Spinner v-if="isAddingFeatures" class="w-4 h-4 mr-2" />
          {{ isAddingFeatures ? 'Saving...' : `Save` }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
